import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { LineReveal } from "@/components/site/Reveal";
import { SITE, waLink } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
        className="relative aspect-[4/5] w-full rounded-[28px] overflow-hidden clip-notch border border-[#39FF14]/20 shadow-[0_40px_120px_-30px_rgba(57,255,20,0.35)]">
        <img src="/img/hero.jpg" alt="Neon-lit electrical installation, Rama Electricals Goregaon East" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-[#030712]/30" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute left-5 right-5 bottom-5 glass rounded-2xl p-5">
          <p className="eyebrow !text-[11px]">The Nova Edit · 2026</p>
          <p className="font-display text-xl mt-2">Power, perfectly wired.</p>
          <p className="text-sm text-[#a5b4c8] mt-1">Cables, switchgear and smart controls, all in stock.</p>
        </div>
      </motion.div>
      <svg className="orbit-ring absolute -inset-10 w-[calc(100%+80px)] h-[calc(100%+80px)] pointer-events-none" viewBox="0 0 200 200" aria-hidden="true">
        <ellipse cx="100" cy="100" rx="96" ry="40" fill="none" stroke="rgba(59,130,246,0.35)" strokeDasharray="2 6" transform="rotate(-24 100 100)" />
        <circle cx="196" cy="100" r="2.5" fill="#39FF14" transform="rotate(-24 100 100)" />
      </svg>
    </div>
  );
};

const CursorGlow = ({ x, y, on }) => (
  <motion.div aria-hidden="true" className="pointer-events-none absolute left-0 top-0 z-0 h-[520px] w-[520px] -ml-[260px] -mt-[260px] rounded-full"
    style={{ x, y, background: "radial-gradient(circle, rgba(57,255,20,0.22) 0%, rgba(57,255,20,0.08) 35%, transparent 70%)", filter: "blur(20px)" }}
    animate={{ opacity: on ? 1 : 0 }} transition={{ duration: 0.5 }} />
);

export const Hero = () => {
  const ref = useRef(null);
  const [glowOn, setGlowOn] = useState(false);
  const gx = useSpring(0, { stiffness: 90, damping: 20 });
  const gy = useSpring(0, { stiffness: 90, damping: 20 });
  const onGlow = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    gx.set(e.clientX - r.left);
    gy.set(e.clientY - r.top);
  };
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  return (
    <section ref={ref} onMouseMove={onGlow} onMouseEnter={() => setGlowOn(true)} onMouseLeave={() => setGlowOn(false)}
      className="relative overflow-hidden min-h-screen flex items-center pt-32 pb-20 px-6" data-testid="hero-section">
      <CursorGlow x={gx} y={gy} on={glowOn} />
      <div className="relative z-10 mx-auto max-w-7xl w-full grid lg:grid-cols-12 gap-14 items-center">
        <motion.div style={{ y, opacity: fade }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="eyebrow flex items-center gap-3 mb-8">
            <span className="pulse-dot h-2 w-2 rounded-full bg-[#39FF14]" /> Electrical Store · Goregaon East, Mumbai
          </motion.p>
          <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-[44px] sm:text-6xl lg:text-[88px]">
            <LineReveal onLoad delay={0.3} lines={["Power your", <span key="n" className="neon-text">space</span>, <span key="o" className="outline-text">brilliantly.</span>]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="mt-8 max-w-xl text-base md:text-lg text-[#a5b4c8] leading-relaxed">
            Rama Electricals brings genuine wires, modular switches, BLDC fans and circuit protection from India's top brands to homes, electricians and businesses across Mumbai.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 1.05 }} className="mt-10 flex flex-wrap gap-4">
            <a href={waLink("Hi Rama Electricals, I'd like a quote for some electrical items.")} target="_blank" rel="noreferrer" className="btn-neon" data-testid="hero-whatsapp-btn">
              <MessageCircle size={18} /> Get a quote on WhatsApp
            </a>
            <Link to="/about" className="btn-ghost" data-testid="hero-about-btn">Our story <ArrowUpRight size={18} /></Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="mt-14 flex items-center gap-6 text-xs font-mono text-[#a5b4c8]">
            <span>GSTIN {SITE.gstin}</span><span className="h-px w-10 bg-white/20" /><span>Retail + Wholesale</span>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 max-w-md mx-auto w-full"><TiltCard /></div>
      </div>
    </section>
  );
};
